import React, { useState, useEffect, useCallback } from 'react';
import { salesApi, customersApi } from '../api/client';
import { Badge, Spin, Empty, useFlash, Modal, F } from '../components/UI';
import { Check } from '../components/Icons';

/* ── localStorage helpers ──────────────────────────────────────── */
const LS_KEY = 'sakthi_fup';
const getLS  = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; } };
const patchLS = (id, patch) => { const d = getLS(); d[id] = { ...d[id], ...patch }; localStorage.setItem(LS_KEY, JSON.stringify(d)); };
const clearLS = id => { const d = getLS(); delete d[id]; localStorage.setItem(LS_KEY, JSON.stringify(d)); };

/* Merge API row with localStorage overrides + customer lookup */
function enrich(s, custMap) {
  const local = getLS()[s.id] || {};
  const cust  = custMap[s.customer_id] || s.customer || {};
  return {
    ...s,
    _name:        cust.full_name    || local.customer_name  || '—',
    _phone:       cust.phone        || local.customer_phone || '—',
    _address:     cust.address      || '',
    _district:    cust.district     || '',
    _code:        cust.customer_code|| '',
    _amount_paid: local.amount_paid  !== undefined ? local.amount_paid  : Number(s.amount_paid  || 0),
    _fup_date:    local.followup_date !== undefined ? local.followup_date : (s.followup_date || null),
  };
}

/* Sort: overdue first → today → upcoming → no date */
const urgencySort = rows =>
  [...rows].sort((a, b) =>
    (a._fup_date || '9999-99-99').localeCompare(b._fup_date || '9999-99-99')
  );

const openWA = phone => {
  const n = String(phone || '').replace(/\D/g, '');
  const num = n.length === 10 ? `91${n}` : n;
  if (num) window.open(`https://wa.me/${num}`, '_blank');
};

const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle:'medium' }) : '—';
const fmtMoney = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function PaymentFUPs() {
  const [rows, setRows]       = useState([]);
  const [custMap, setCustMap] = useState({});
  const [busy, setBusy]       = useState(true);
  const { show, Flash }       = useFlash();
  const [detail, setDetail]   = useState(null);
  const [newDate, setNewDate] = useState('');
  const [payAmt, setPayAmt]   = useState('');
  const [payMode, setPayMode] = useState('');
  const [dateSaving, setDS]   = useState(false);
  const [paySaving, setPS]    = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [sRes, cRes] = await Promise.all([
        salesApi.list({ page_size: 500 }),
        customersApi.list({ page_size: 2000 }),
      ]);
      const map = {};
      (cRes.data || []).forEach(c => { map[c.id] = c; });
      setCustMap(map);
      setRows((sRes.data || []).filter(s =>
        s.payment_status === 'partial' || s.payment_status === 'pending'
      ));
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enriched = urgencySort(rows.map(s => enrich(s, custMap)));
  const today    = new Date().toISOString().split('T')[0];
  const totalBal = enriched.reduce((a, s) => a + Number(s.total) - s._amount_paid, 0);

  /* Record partial payment */
  const recordPayment = async () => {
    if (!payAmt || Number(payAmt) <= 0) { show('Enter a valid amount.', 'error'); return; }
    setPS(true);
    try {
      const newPaid  = detail._amount_paid + Number(payAmt);
      const balance  = Number(detail.total) - newPaid;
      const newStatus = balance <= 0 ? 'paid' : 'partial';

      patchLS(detail.id, { amount_paid: newPaid, payment_mode: payMode || undefined });
      try { await salesApi.update(detail.id, { amount_paid: newPaid, payment_status: newStatus, ...(payMode && { payment_mode: payMode }) }); } catch {}

      if (newStatus === 'paid') {
        clearLS(detail.id);
        setRows(r => r.filter(x => x.id !== detail.id));
        setDetail(null);
        show('Full payment received — marked as paid!');
      } else {
        const updated = enrich({ ...detail, amount_paid: newPaid, payment_status: newStatus }, custMap);
        setRows(r => r.map(x => x.id === detail.id ? { ...x, amount_paid: newPaid, payment_status: newStatus } : x));
        setDetail(updated);
        setPayAmt('');
        setPayMode('');
        show(`${fmtMoney(payAmt)} recorded. Balance remaining: ${fmtMoney(balance)}`);
      }
    } catch { show('Failed to record payment.', 'error'); }
    finally { setPS(false); }
  };

  /* Save / update follow-up date */
  const saveDate = async () => {
    if (!newDate) return;
    setDS(true);
    try {
      patchLS(detail.id, { followup_date: newDate });
      try { await salesApi.update(detail.id, { followup_date: newDate }); } catch {}
      setRows(r => r.map(x => x.id === detail.id ? { ...x, followup_date: newDate } : x));
      setDetail(d => ({ ...d, _fup_date: newDate, followup_date: newDate }));
      setNewDate('');
      show('Follow-up date saved!');
    } finally { setDS(false); }
  };

  if (busy) return <Spin/>;

  return (
    <div>
      <Flash/>
      <div className="page-hd"><h2>Payment Follow-Ups</h2></div>

      {/* Stats */}
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)' }}>
        <div className="stat-card"><div className="stat-lbl">Total Pending</div><div className="stat-val">{enriched.length}</div></div>
        <div className="stat-card"><div className="stat-lbl">Overdue</div><div className="stat-val" style={{ color:'var(--red)' }}>{enriched.filter(r => r._fup_date && r._fup_date < today).length}</div></div>
        <div className="stat-card"><div className="stat-lbl">Due Today</div><div className="stat-val" style={{ color:'orange' }}>{enriched.filter(r => r._fup_date === today).length}</div></div>
        <div className="stat-card"><div className="stat-lbl">Total Balance</div><div className="stat-val" style={{ fontSize:16, color:'var(--red)' }}>₹{totalBal.toLocaleString('en-IN')}</div></div>
      </div>

      {enriched.length === 0
        ? <div className="card card-0"><Empty msg="No pending payment follow-ups — all cleared!"/></div>
        : <div className="card card-0">
            <div className="tbl-wrap"><table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Invoice</th>
                  <th>Total</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Follow-up Date</th>
                </tr>
              </thead>
              <tbody>
                {enriched.map(s => {
                  const balance   = Number(s.total) - s._amount_paid;
                  const isOverdue = s._fup_date && s._fup_date < today;
                  const isToday   = s._fup_date === today;
                  return (
                    <tr key={s.id} style={{ cursor:'pointer', background: isOverdue ? 'rgba(220,53,69,0.05)' : isToday ? 'rgba(255,165,0,0.07)' : undefined }}
                      onClick={() => { setDetail(s); setNewDate(''); setPayAmt(''); setPayMode(''); }}>
                      <td style={{ fontWeight:600 }}>{s._name}</td>
                      <td style={{ fontSize:12, color:'var(--text2)' }}>{s._phone}</td>
                      <td style={{ fontSize:11, fontFamily:'monospace', color:'var(--text2)', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {s.sale_code || `#${String(s.id).slice(0,8)}`}
                      </td>
                      <td style={{ fontWeight:600 }}>{fmtMoney(s.total)}</td>
                      <td style={{ color:'var(--green)' }}>{fmtMoney(s._amount_paid)}</td>
                      <td style={{ color:'var(--red)', fontWeight:600 }}>{fmtMoney(balance)}</td>
                      <td><Badge label={s.payment_status}/></td>
                      <td>
                        {s._fup_date
                          ? <span style={{ fontSize:12, color: isOverdue ? 'var(--red)' : isToday ? 'orange' : 'inherit', fontWeight: (isOverdue||isToday) ? 700 : 400 }}>
                              {fmtDate(s._fup_date)}
                              {isOverdue && <span style={{ fontSize:10, marginLeft:5, background:'var(--red)', color:'#fff', borderRadius:3, padding:'1px 5px' }}>Overdue</span>}
                              {isToday   && <span style={{ fontSize:10, marginLeft:5, background:'orange', color:'#fff', borderRadius:3, padding:'1px 5px' }}>Today</span>}
                            </span>
                          : <span style={{ color:'var(--text2)', fontSize:12 }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
      }

      {/* ── Detail Modal ── */}
      <Modal open={!!detail} onClose={() => setDetail(null)}
        title="Follow-up Details"
        footer={<button className="btn" onClick={() => setDetail(null)}>Close</button>}
      >
        {detail && (
          <div style={{ fontSize:13 }}>

            {/* Customer */}
            <div style={{ background:'var(--surface)', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)', marginBottom:6 }}>{detail._name}</div>
              {detail._phone !== '—' && (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                  <span style={{ fontWeight:500 }}>{detail._phone}</span>
                  <button className="btn btn-sm" style={{ fontSize:11 }} onClick={() => window.open(`tel:${detail._phone}`)}>Call</button>
                  <button className="btn btn-sm" style={{ fontSize:11, background:'#25d366', color:'#fff', border:'none' }} onClick={() => openWA(detail._phone)}>WhatsApp</button>
                </div>
              )}
              {detail._code    && <div style={{ color:'var(--text2)', fontSize:12 }}>ID: {detail._code}</div>}
              {detail._address && <div style={{ color:'var(--text2)', fontSize:12, marginTop:2 }}>{detail._address}</div>}
              {detail._district&& <div style={{ color:'var(--text2)', fontSize:12 }}>{detail._district}</div>}
            </div>

            {/* Sale info */}
            <div style={{ marginBottom:12, fontSize:12, display:'flex', gap:16, flexWrap:'wrap', color:'var(--text2)' }}>
              <span>Invoice: <strong style={{ color:'#111', fontFamily:'monospace' }}>{detail.sale_code || `#${String(detail.id).slice(0,8)}`}</strong></span>
              <span>Date: <strong style={{ color:'#111' }}>{new Date(detail.created_at).toLocaleDateString('en-IN',{dateStyle:'medium'})}</strong></span>
              <span>Mode: <strong style={{ color:'#111' }}>{detail.payment_mode || '—'}</strong></span>
            </div>

            {/* Items */}
            {detail.items && detail.items.length > 0 && (
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:14, fontSize:12 }}>
                <thead>
                  <tr style={{ borderBottom:'2px solid var(--border)' }}>
                    <th style={{ textAlign:'left', padding:'5px 6px', color:'var(--text2)', fontWeight:500 }}>Product</th>
                    <th style={{ textAlign:'center', padding:'5px 6px', color:'var(--text2)', fontWeight:500 }}>Qty</th>
                    <th style={{ textAlign:'right', padding:'5px 6px', color:'var(--text2)', fontWeight:500 }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((item, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'5px 6px' }}>{item.product?.name || '—'}</td>
                      <td style={{ padding:'5px 6px', textAlign:'center' }}>{item.qty}</td>
                      <td style={{ padding:'5px 6px', textAlign:'right' }}>{fmtMoney(item.qty * item.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Payment summary */}
            <div style={{ display:'flex', gap:10, marginBottom:10 }}>
              {[
                { lbl:'Total',   val: fmtMoney(detail.total),                                          color:'var(--navy)'  },
                { lbl:'Paid',    val: fmtMoney(detail._amount_paid),                                   color:'var(--green)' },
                { lbl:'Balance', val: fmtMoney(Number(detail.total) - detail._amount_paid),            color:'var(--red)'   },
              ].map(c => (
                <div key={c.lbl} style={{ flex:1, background:'var(--surface)', borderRadius:6, padding:'8px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:11, color:'var(--text2)', marginBottom:3 }}>{c.lbl}</div>
                  <div style={{ fontWeight:700, color:c.color, fontSize:14 }}>{c.val}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom:14, fontSize:12, display:'flex', alignItems:'center', gap:8 }}>
              <strong>Status:</strong> <Badge label={detail.payment_status}/>
            </div>

            {/* Record payment */}
            <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
              <div style={{ fontWeight:600, fontSize:12, color:'var(--text2)', textTransform:'uppercase', marginBottom:10 }}>Record Payment</div>
              <div style={{ display:'flex', gap:8, alignItems:'flex-end', flexWrap:'wrap' }}>
                <div style={{ flex:'1 1 140px' }}>
                  <F label="Amount Received Now (₹)">
                    <input type="number" min="1" placeholder={`Max ₹${(Number(detail.total) - detail._amount_paid).toLocaleString('en-IN')}`}
                      value={payAmt} onChange={e => setPayAmt(e.target.value)}/>
                  </F>
                </div>
                <div style={{ flex:'1 1 120px' }}>
                  <F label="Payment Type">
                    <select value={payMode} onChange={e => setPayMode(e.target.value)}>
                      <option value="">— Select —</option>
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="other">Other</option>
                    </select>
                  </F>
                </div>
                <button className="btn btn-primary" onClick={recordPayment} disabled={paySaving || !payAmt} style={{ marginBottom:1 }}>
                  {paySaving ? 'Saving…' : <><Check s={13}/> Record</>}
                </button>
              </div>
              {payAmt && Number(payAmt) > 0 && (
                <div style={{ marginTop:6, fontSize:12, color:'var(--text2)' }}>
                  After this payment — Balance:&nbsp;
                  <strong style={{ color: (Number(detail.total) - detail._amount_paid - Number(payAmt)) <= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {fmtMoney(Math.max(0, Number(detail.total) - detail._amount_paid - Number(payAmt)))}
                  </strong>
                  {(Number(detail.total) - detail._amount_paid - Number(payAmt)) <= 0 && <span style={{ color:'var(--green)', marginLeft:6 }}>✓ Fully paid!</span>}
                </div>
              )}
            </div>

            {/* Follow-up date */}
            <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontWeight:600, fontSize:12, color:'var(--text2)', textTransform:'uppercase', marginBottom:8 }}>Follow-up Date</div>
              {detail._fup_date && (
                <div style={{ marginBottom:10, fontSize:13 }}>
                  Current:&nbsp;<strong>{fmtDate(detail._fup_date)}</strong>
                </div>
              )}
              <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                <div style={{ flex:1 }}>
                  <F label={detail._fup_date ? 'Reschedule to a new date' : 'Set Follow-up Date'}>
                    <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}/>
                  </F>
                </div>
                <button className="btn btn-primary" onClick={saveDate} disabled={dateSaving || !newDate} style={{ marginBottom:1 }}>
                  {dateSaving ? 'Saving…' : 'Save Date'}
                </button>
              </div>
            </div>

          </div>
        )}
      </Modal>
    </div>
  );
}
